// components/form/Form.tsx
import React, { SetStateAction } from 'react'
import { Button } from '../../../../../common/components/button/Button'
import { ComplexOrderInterface } from '../../../../../common/types/interfaces'
import classes from './Form.module.sass'
import { format } from 'date-fns'
import { IconButton } from '../../../../../common/components/iconButton/IconButton'
import close from '../../../../../assets/images/icons/close.svg'
import add from '../../../../../assets/images/icons/add.svg'
import { StatusSelector } from './statusSelector/StatusSelector'
import { createTypeSelectList } from '../../../../../common/utils/functions/createTypeSelectList'
import { ComplexOrderStatus } from '../../../../../common/types/enums'
import { SearchSelect } from '../../../../../common/components/searchSelect/SearchSelect'
import { useSelector } from 'react-redux'
import { selectDarkTheme } from '../../../../../store/slices/settingsSlice'
import { Input } from '../../../../../common/components/input/Input'
import { Textarea } from '../../../../../common/components/textarea/Textarea'
import { useForm } from './hooks/useForm'

type Props = {
  edit: boolean
  data?: ComplexOrderInterface
  setData: React.Dispatch<SetStateAction<ComplexOrderInterface | undefined>>
  getList: () => void
  setModalActive: React.Dispatch<SetStateAction<boolean>>
  el?: ComplexOrderInterface
  initialData: ComplexOrderInterface
  modalActive: boolean
}

const getStatusText = (status: ComplexOrderStatus): string => {
  const map: Record<number, string> = {
    [ComplexOrderStatus.DRAFT]: 'Новая',
    [ComplexOrderStatus.SENT_TO_MANAGER]: 'Отправлена',
    [ComplexOrderStatus.IN_PROGRESS]: 'В работе',
    [ComplexOrderStatus.APPROVED]: 'Согласована',
    [ComplexOrderStatus.CONVERTED]: 'Конвертирована',
    [ComplexOrderStatus.ARCHIVED]: 'Архив',
  }
  return map[status] || 'Неизвестно'
}

const getStatusColor = (status: ComplexOrderStatus): string => {
  const map: Record<number, string> = {
    [ComplexOrderStatus.DRAFT]: '#3498db',
    [ComplexOrderStatus.SENT_TO_MANAGER]: '#3498db',
    [ComplexOrderStatus.IN_PROGRESS]: '#f39c12',
    [ComplexOrderStatus.APPROVED]: '#2ecc71',
    [ComplexOrderStatus.CONVERTED]: '#2ecc71',
    [ComplexOrderStatus.ARCHIVED]: '#95a5a6',
  }
  return map[status] || '#95a5a6'
}

export const Form = ({
  edit,
  data,
  setData,
  getList,
  setModalActive,
  el,
  modalActive,
  initialData,
}: Props) => {
  const {
    formAction,
    inputHandler,
    setDirty,
    getValidationResult,
    disabled,
    deleteItem,
    adding,
    setAdding,
    createSaleItemSelectList,
    handleAddItem,
    updateItem,
    listRef,
    getDisabledStatuses,
  } = useForm(initialData, setData, edit, getList, setModalActive, modalActive, data, el)

  const dark = useSelector(selectDarkTheme)

  const clientFields = data?.data?.filter((d) => d.type === 'custom') || []

  if (data)
    return (
      <div className={classes.container}>
        <div className={classes.title}>{el ? 'редактирование заявки' : 'создание заявки'}</div>
        {el && (
          <div className={classes.id}>
            <div className={classes.label}>id</div>
            <div className={classes.value}>{el.id}</div>
          </div>
        )}

        <div className={classes.title}>статус заявки</div>
        <StatusSelector
          disabledOptions={getDisabledStatuses()}
          current={Number(data?.status)}
          options={createTypeSelectList(ComplexOrderStatus, getStatusText)}
          onClick={(value: number) => inputHandler(value, 'status', 'number')}
          colorSelector={getStatusColor}
        />

        <div className={classes.title}>контактные данные</div>
        <Input
          type={'text'}
          fieldName={'phone_number'}
          value={String(data.phone_number || '')}
          placeHolder={'Телефон'}
          onChangeHandler={inputHandler}
          itemValidationResult={getValidationResult('phone_number')}
          onBlur={setDirty}
        />
        <Input
          type={'text'}
          fieldName={'customerName'}
          value={String(data.customerName || '')}
          placeHolder={'Имя клиента'}
          onChangeHandler={inputHandler}
          itemValidationResult={getValidationResult('customerName')}
          onBlur={setDirty}
        />

        {el && (
          <div className={classes.row}>
            {el.createdAt && (
              <div className={classes.item}>
                <div className={classes.label}>Создана</div>
                <div className={classes.value}>
                  {format(new Date(el.createdAt), 'dd.MM.yyy HH:mm')}
                </div>
              </div>
            )}
            {el.updatedAt && (
              <div className={classes.item}>
                <div className={classes.label}>Обновлена</div>
                <div className={classes.value}>
                  {format(new Date(el.updatedAt), 'dd.MM.yyy HH:mm')}
                </div>
              </div>
            )}
          </div>
        )}

        <div className={classes.title}>состав заявки</div>
        {data.items && data.items.length > 0 && (
          <div className={classes.list} ref={listRef}>
            {data.items.map((item) => (
              <div key={item.itemId}>
                <div className={classes.row}>
                  <div className={classes.content}>
                    <div className={classes.name}>
                      <div className={classes.label}>Наименование</div>
                      <div className={classes.itemValue}>{item.name}</div>
                    </div>
                    <div className={classes.item}>
                      <div className={classes.label}>Кол-во</div>
                      <input
                        type="text"
                        value={item.quantity === 0 ? '' : String(item.quantity)}
                        onChange={(e) => updateItem(item.itemId, 'quantity', e.target.value)}
                        className={dark ? classes.inputDark : classes.input}
                      />
                    </div>
                    <div className={classes.item}>
                      <div className={classes.label}>Базовая</div>
                      <div className={classes.itemValue}>
                        {item.basePrice.toLocaleString('ru-RU')} ₽
                      </div>
                    </div>
                    <div className={classes.item}>
                      <div className={classes.label}>Ручная</div>
                      <input
                        type="text"
                        value={
                          item.manualPrice === null || item.manualPrice === 0
                            ? ''
                            : String(item.manualPrice)
                        }
                        onChange={(e) => updateItem(item.itemId, 'manualPrice', e.target.value)}
                        className={dark ? classes.inputDark : classes.input}
                      />
                    </div>
                    <div className={classes.item}>
                      <div className={classes.label}>Сумма</div>
                      <div className={classes.itemValue}>
                        {item.total.toLocaleString('ru-RU')} ₽
                      </div>
                    </div>
                  </div>
                  <IconButton
                    small
                    onClick={() => deleteItem(item.itemId)}
                    alt="Удалить"
                    display
                    icon={close}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {!adding ? (
          <IconButton small onClick={() => setAdding(true)} alt="Добавить" display icon={add} />
        ) : (
          <div>
            <SearchSelect
              fieldName="saleItemId"
              options={createSaleItemSelectList()}
              onChangeHandler={handleAddItem}
              type="add_item"
              placeholder={
                createSaleItemSelectList().length
                  ? 'Начните ввод и выберите товар'
                  : 'Товары не найдены'
              }
            />
            <IconButton small onClick={() => setAdding(false)} alt="Отмена" display icon={close} />
          </div>
        )}

        <div className={classes.row}>
          <div className={classes.item}>
            <div className={classes.label}>Сумма заявки</div>
            <div className={classes.value}>{data.total?.toLocaleString('ru-RU')} ₽</div>
          </div>
        </div>

        {clientFields.length > 0 && (
          <>
            <div className={classes.title}>поля, заполненные клиентом</div>
            {clientFields.map((field, index) => (
              <div key={index} className={classes.row}>
                <div className={classes.item}>
                  <div className={classes.label}>Поле {field.fieldTemplateId || index + 1}</div>
                  <div className={classes.value}>{field.value}</div>
                </div>
              </div>
            ))}
          </>
        )}

        <div className={classes.title}>комментарий</div>
        <Textarea
          type={'textarea'}
          fieldName={'notes'}
          value={String(data.notes || '')}
          placeHolder="Комментарий"
          rows={2}
          cols={20}
          onChangeHandler={inputHandler}
        />

        <Button text={el ? 'Сохранить' : 'Создать'} onClick={formAction} disabled={disabled} />
      </div>
    )
}
